import {Exclude, Expose, instanceToPlain, plainToClass, Type} from "class-transformer";
import * as t from "io-ts";
import {v4 as uuid} from "uuid";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {Entity} from "../entity";
import {PipelineGraphEdgeArrayValidator, PipelineGraphEdgeEntity} from "./pipeline-graph-edge";
import {PipelineGraphNodeArrayValidator, PipelineGraphNodeEntity} from "./pipeline-graph-node";

export const PipelineGraphValidator = t.intersection([
  t.type({
    id: t.string,
    pipelineId: t.string,
    effectiveDttm: t.number,
    displayName: t.string,
  }),
  t.partial({
    nodes: PipelineGraphNodeArrayValidator,
    edges: PipelineGraphEdgeArrayValidator,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const PipelineGraphArrayValidator = t.array(PipelineGraphValidator);

export type PipelineGraph = t.TypeOf<typeof PipelineGraphValidator>;
export type PipelineGraphArray = t.TypeOf<typeof PipelineGraphArrayValidator>;

@Exclude()
export class PipelineGraphEntity implements Entity {
  @Expose() id!: string;
  @Expose() pipelineId!: string;
  @Expose({name: 'displayName'}) displayableName!: string;
  @Expose() effectiveDttm!: number;
  @Expose()
  @Type(() => PipelineGraphNodeEntity)
  nodes: PipelineGraphNodeEntity[] = [];
  @Expose()
  @Type(() => PipelineGraphEdgeEntity)
  edges: PipelineGraphEdgeEntity[] = [];
  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;

  readonly entityType = 'PIPELINEGRAPH';

  get displayName(): Readonly<string> {
    return this.displayableName;
  }

  /**
   * Constructor for PipelineGraphEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static builder(
    pipelineId: string,
    displayableName: string,
    id: string = uuid()
  ): EffectiveDttmBuilderStep {
    return new PipelineGraphBuilder(pipelineId, displayableName, id);
  }

  static create(builder: PipelineGraphBuilder): PipelineGraphEntity {
    const entity = new PipelineGraphEntity();
    entity.id = builder.id;
    entity.pipelineId = builder.pipelineId;
    entity.displayableName = builder.displayableName;
    entity.effectiveDttm = builder.effectiveDttm;
    entity.nodes = builder.nodes;
    entity.edges = builder.edges;
    entity.createDttm = builder.createDttm;
    entity.lastModifiedDttm = builder.lastModifiedDttm;
    return entity;
  }

  static from(graph: PipelineGraph): PipelineGraphEntity {
    const entity: PipelineGraphEntity = plainToClass(
      PipelineGraphEntity,
      graph,
      {
        excludeExtraneousValues: true,
      }
    );
    return entity;
  }

  loadPlainValue(): Promise<PipelineGraph> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(PipelineGraphValidator, object);
  }

  asPlainValue(): PipelineGraph {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSONNow(PipelineGraphValidator, object);
  }
}

interface EffectiveDttmBuilderStep {
  setEffectiveDttm: (value: number) => PipelineGraphBuilder;
}

class PipelineGraphBuilder implements EffectiveDttmBuilderStep {
  id: string;
  pipelineId!: string;
  displayableName!: string;
  effectiveDttm!: number;
  nodes: PipelineGraphNodeEntity[] = [];
  edges: PipelineGraphEdgeEntity[] = [];
  createDttm?: number = undefined;
  lastModifiedDttm?: number = undefined;

  constructor(
    pipelineId: string,
    displayableName: string,
    id: string = uuid()
  ) {
    this.id = id;
    this.pipelineId = pipelineId;
    this.displayableName = displayableName;
  }

  setEffectiveDttm(value: number): PipelineGraphBuilder {
    this.effectiveDttm = value;
    return this;
  }

  setNodes(value: PipelineGraphNodeEntity[]): PipelineGraphBuilder {
    this.nodes = value;
    return this;
  }

  setEdges(value: PipelineGraphEdgeEntity[]): PipelineGraphBuilder {
    this.edges = value;
    return this;
  }

  setCreateDttm(value: number | undefined): PipelineGraphBuilder {
    this.createDttm = value;
    return this;
  }

  setLastModifiedDttm(value: number | undefined): PipelineGraphBuilder {
    this.lastModifiedDttm = value;
    return this;
  }

  /* istanbul ignore next */
  private isValid(): boolean {
    return (
      this.id != undefined &&
      this.pipelineId != undefined &&
      this.effectiveDttm != undefined &&
      this.displayableName != undefined
    );
  }

  build(): PipelineGraphEntity {
    if (!this.isValid()) {
      throw Error(
        'The following fields must be defined: ID, PipelineID, EffectiveDttm, & DisplaybleName.'
      );
    }

    return PipelineGraphEntity.create(this);
  }
}
